/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import DoneIcon from '@mui/icons-material/Done';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { StatCardV2 } from '../../../components/v2';
import TableService from '../../../services/TableService';
import {
  REQUISICAO_STATUS_LABELS,
  REQUISICAO_STATUS_ORDER,
} from './statusLabels';

const STATUS_CARD = {
  AGUARDANDO: { color: 'info', icon: <InfoIcon /> },
  PARCIALMENTE_APROVADA: { color: 'warning', icon: <HourglassTopIcon /> },
  APROVADA: { color: 'success', icon: <DoneIcon /> },
  REJEITADA: { color: 'error', icon: <CancelIcon /> },
  EXECUTANDO: { color: 'info', icon: <InfoIcon /> },
  FINALIZADA: { color: 'success', icon: <DoneIcon /> },
  CANCELADA: { color: 'error', icon: <ReportProblemIcon /> },
};

const EMPTY_COUNTS = {
  filtrado: {},
  total: {},
};

const normalizeResumo = (data) => {
  if (!data || typeof data !== 'object') {
    return EMPTY_COUNTS;
  }
  if (data.filtrado != null || data.total != null) {
    return {
      filtrado: data.filtrado && typeof data.filtrado === 'object' ? data.filtrado : {},
      total: data.total && typeof data.total === 'object' ? data.total : {},
    };
  }
  return { filtrado: data, total: {} };
};

const countForStatus = (bucket, status) => {
  if (status === 'AGUARDANDO') {
    const keys = ['AGUARDANDO', 'ENVIADO', 'RASCUNHO', 'EM_ANALISE'];
    if (!keys.some((key) => bucket[key] != null)) return null;
    return keys.reduce((acc, key) => acc + (Number(bucket[key]) || 0), 0);
  }
  if (status === 'FINALIZADA') {
    const keys = ['FINALIZADA', 'CONCLUIDA'];
    if (!keys.some((key) => bucket[key] != null)) return null;
    return keys.reduce((acc, key) => acc + (Number(bucket[key]) || 0), 0);
  }
  return bucket[status];
};

const formatCardValue = (filtrado, total) => {
  if (filtrado == null && total == null) {
    return { valueFiltered: null, valueTotal: null };
  }
  return {
    valueFiltered: filtrado ?? 0,
    valueTotal: total ?? null,
  };
};

const ResumoCards = ({ apiFilterBase, activeStatus, onFilterByStatus, refreshKey }) => {
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [loading, setLoading] = useState(false);

  const fetchCounts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await TableService.request('requisicoes/resumo', apiFilterBase);
      setCounts(normalizeResumo(response?.data));
    } catch {
      setCounts(EMPTY_COUNTS);
    } finally {
      setLoading(false);
    }
  }, [apiFilterBase, refreshKey]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  return (
    <Grid container spacing={2}>
      {REQUISICAO_STATUS_ORDER.map((status) => {
        const card = STATUS_CARD[status];
        const cardValues = formatCardValue(
          countForStatus(counts.filtrado, status),
          countForStatus(counts.total, status)
        );
        return (
          <Grid key={status} size={{ xs: 6, sm: 4, md: 12 / 7 }}>
            <StatCardV2
              label={REQUISICAO_STATUS_LABELS[status]}
              valueFiltered={cardValues.valueFiltered}
              valueTotal={cardValues.valueTotal}
              totalCaption="no total"
              valueFontSize="1.25rem"
              icon={card.icon}
              color={card.color}
              loading={loading}
              active={activeStatus === status}
              onClick={() =>
                onFilterByStatus(activeStatus === status ? null : status)
              }
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

ResumoCards.propTypes = {
  apiFilterBase: PropTypes.object.isRequired,
  activeStatus: PropTypes.string,
  onFilterByStatus: PropTypes.func.isRequired,
  refreshKey: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
};

ResumoCards.defaultProps = {
  activeStatus: null,
  refreshKey: false,
};

export default ResumoCards;
